<template>
	<div class="item">
		<div class="item-label">
			<b-form-checkbox
				:checked="isChecked"
				:class="checkboxClasses"
				ref="checkedCheckbox"
				@change="checkedChanged"
			>
				<template v-if="nothing">{{ nothingAllowedText }}</template>
				<template v-else>
					<span class="item-label-text">
						<Label
							:characteristic="characteristic"
							:label-type="labelType"
							:highlight-words="highlightWords"
						/>
					</span>
					<span v-if="hasIcon" class="item-label-icon">
						<img
							:src="iconPath + '/' + characteristic.naturalId + '.svg'"
							:alt="label"
						/>
					</span>
					<span
						v-if="characteristic.description"
						class="item-label-description"
						:title="!descriptionConfig || !descriptionConfig.inline ? characteristic.description : undefined"
						v-b-tooltip.html
					>
						<template v-if="descriptionConfig && descriptionConfig.inline">
							{{ characteristic.description | stripTags }}
						</template>
						<Icon v-else icon="info"/>
					</span>
				</template>
			</b-form-checkbox>
			<a
				v-if="isChecked && !nothing && hasChilds && !isMaxLevelReached"
				href="#"
				@click.prevent="addParent({ characteristic: characteristic, onClose: onCloseParent })"
			>
				<Icon icon="pencil"/>
			</a>
		</div>
		<div v-if="searchHits" class="item-search-hits">
			Gefundene Treffer:
			<ul>
				<li
					v-for="characteristic in searchHits"
					:key="characteristic.id"
				>
					<Label
						:characteristic="characteristic"
						:highlight-words="highlightWords"
					/>
				</li>
			</ul>
		</div>
		<ul
			v-if="!nothing && isChecked && nothingAllowed && !hasSelectedChildsToLastLevel && hasChilds && !isMaxLevelReached"
			class="characteristic-selected-items"
		>
			<li>
				<a href="#" @click.prevent="addParent({characteristic: characteristic, onClose: onCloseParent})">
					{{ nothingAllowedTextNextLevel }}
				</a>
				<Additions
					v-if="supplementAdditions"
					:is-list-styling="false"
					:supplements="supplementAdditions"
				/>
			</li>
		</ul>
		<SelectedChilds
			v-if="!nothing && hasSelectedChildsToLastLevel && !isMaxLevelReached"
			:characteristic="characteristic"
			:selected="selected"
			:styling="selectedItemsStylingList"
			:label-type="labelType"
			:show-experience="selectedItemsConfig ? selectedItemsConfig.showExperience : undefined"
			@editItem="addParent({characteristic: characteristic, onClose: onCloseParent})"
		/>
		<div v-if="mustChooseSupplements">
			<Supplements
				v-for="itemCharacteristicType in relevantItemCharacteristicTypes"
				:key="itemCharacteristicType.id"
				:item-characteristic-type="itemCharacteristicType"
				:initial-selected="getInitialSupplements(itemCharacteristicType)"
				:nothing-allowed="supplementsConfig && supplementsConfig.nothingAllowed !== undefined ? supplementsConfig.nothingAllowed : true"
				:label-type="labelType"
				:with-experience="withExperience"
				@input="setSupplements"
			/>
			<div v-if="withExperience" class="item-supplements-experience">
				<Select
					:value="selectedItem.experience"
					:options="experienceOptions"
					empty-value-label="Welche Erfahrung ist erforderlich?"
					smaller
					@change="experienceChanged"
				/>
			</div>
		</div>
		<div v-if="nothing" class="item-nothing-choose">
			<template v-if="showMultipleHint">
				Oder <span class="font-weight-extra-bold">Mehrfachauswahl</span> möglich:
			</template>
			<template v-else>Oder wähle aus:</template>
		</div>
		<div
			v-if="mustChooseAdditions || (selectedItem && selectedItem.experience && !withExperience)"
			class="item-additions"
		>
			<ul v-if="selectedItem" class="characteristic-selected-items no-left-padding">
				<characteristic-selected-items-item
					:item="selectedItem"
					:selected="[]"
					:show-item-label="false"
					:with-edit-button="mustChooseAdditions"
					:styling="selectedItemsStylingList"
					:label-type="labelType"
					@edit="editAdditions(false)"
				/>
			</ul>
		</div>
		<div
			v-if="characteristic.hint && isChecked"
			v-html="characteristic.hint"
			class="item-hint"
		/>
		<DeselectPopUp
			v-if="confirmDeselect"
			:characteristic="characteristic"
			@edit="editAlreadySelected"
			@remove="onDeSelectCharacteristic(characteristic)"
			@hidden="dismissDeselectPopUp"
		/>
	</div>
</template>

<script>
import SingleSelect from "@/vue/characteristic/SingleSelect";
import Supplements from "@/vue/characteristic/Supplements";
import _ from "lodash";
import {isDefined} from "@/helper/Value";
import {mapActions, mapMutations, mapState} from "vuex";
import Label from "@/vue/characteristic/Label";
import {flatten} from "@/helper/characteristic/Flatten";
import {hasSelectedChildsToLastLevel, isSelected} from "@/helper/characteristic/Characteristic";
import {getChildIdsToLastLevel, getParents} from "@/helper/application/characteristic/Provider";
import SelectedChilds from "@/vue/characteristic/SelectedChilds";
import Dropdown from "@/vue/characteristic/Dropdown";
import {get} from "@/helper/characteristic/edit/LabelSet";
import Select from "@/vue/common/form/Select";
import {getOptions} from "@/helper/characteristic/Requirement";
import Icon from "@/vue/icon/Icon";
import {search} from "@/helper/characteristic/Search";
import {ADDITIONS_EDUCATION_DISABLE, hasTag} from "@/helper/characteristic/Tag";
import DeselectPopUp from "@/vue/characteristic/DeselectPopUp.vue";
import {STYLING__LIST} from "@/vue/characteristic/SelectedItems"
import * as Event from "@/helper/tracking/Event";
import EventData from "@/helper/tracking/EventData";
import Scale from "@/vue/characteristic/Scale.vue";
import Additions from "@/vue/characteristic/selected-items/Additions.vue";
import {getAsList} from "@/helper/characteristic/Supplement";
import * as LabelHelper from "@/helper/characteristic/Label"
import {isEmptyOrNull} from "@/helper/String";

export default {

	components: {
		Supplements,
		Additions,
		Scale,
		DeselectPopUp,
		Icon,
		Select,
		Dropdown,
		SelectedChilds,
		SingleSelect,
		Label,
	},

	props: {
		characteristic: {
			type: Object,
			required: true,
		},
		level: {
			type: Number,
			required: true,
		},
		nothing: {
			type: Boolean,
			required: false,
			default: false,
		},
		keyword: {
			type: String,
			required: false
		}
	},

	data ()
	{
		return {
			confirmDeselect: false,
		};
	},

	computed: {
		...mapState('meta', ['meta']),
		...mapState('characteristic/fan', [
			'characteristicType',
			'characteristicItems',
			'characteristicMetaProperty',
			'nothingAllowed',
			'supplementsConfig',
			'withExperience',
			'withSelfAssessment',
			'withEducation',
			'withRequirement',
			'experienceConfig',
			'singleChoice',
			'parents',
			'labelType',
			'labelSet',
			'iconPath',
			'descriptionConfig',
			'showMultipleHint',
			'additionsConfig',
			'maxLevel',
			'selectedItemsConfig'
		]),
		selected: {
			get ()
			{
				return this.$store.state.characteristic.fan.selected;
			},

			set (value)
			{
				this.setSelected(value);
			}
		},
		selectedItem ()
		{
			return _.find(
				this.selected,
				item => item.item.id === this.characteristic.id
			);
		},
		experienceOptions ()
		{
			return _.map(this.meta.workExperience, characteristicItem =>
			{
				return {
					value: characteristicItem,
					text: LabelHelper.get(characteristicItem, this.labelType)
				};
			});
		},
		requirementOptions ()
		{
			return getOptions();
		},
		nothingAllowedText ()
		{
			return this.getNothingAllowedTextByLevel(this.level);
		},
		nothingAllowedTextNextLevel ()
		{
			return this.getNothingAllowedTextByLevel(this.level + 1);
		},
		isNothingAllowed ()
		{
			if (!this.nothingAllowed)
			{
				return false;
			}

			return this.nothingAllowed.onlyOverall !== true;
		},
		isSelfChecked ()
		{
			return isDefined(
				_.find(this.selected, item => item.item.id === this.characteristic.id)
			);
		},
		isChecked ()
		{
			if (this.nothing)
			{
				return this.isSelfChecked && !this.hasSelectedChildsToLastLevel;
			}

			return this.isSelfChecked || this.hasSelectedChildsToLastLevel;
		},
		hasChilds ()
		{
			return this.characteristic.children.length > 0;
		},
		hasSelectedChildsToLastLevel ()
		{
			return hasSelectedChildsToLastLevel(this.selected, this.characteristic);
		},
		checkboxClasses ()
		{
			return {
				'has-childs': !this.nothing && this.hasChilds,
			};
		},
		relevantItemCharacteristicTypes ()
		{
			const itemCharacteristicTypes = this.characteristic.itemCharacteristicTypes;

			if (!this.supplementsConfig?.types)
			{
				return itemCharacteristicTypes;
			}

			return _.filter(
				itemCharacteristicTypes,
				item =>
				{
					return _.includes(this.supplementsConfig.types, item.type.id);
				}
			);
		},
		hasItemCharacteristicTypes ()
		{
			return this.relevantItemCharacteristicTypes.length > 0;
		},
		disableSupplements ()
		{
			return this.supplementsConfig?.disable;
		},
		mustChooseAdditions ()
		{
			return this.mustChooseExperience
				|| this.mustChooseSelfAssessment
				|| this.mustChooseEducation
				|| this.mustChooseRequirement;
		},
		mustChooseExperience ()
		{
			const supplementsEligible = this.hasItemCharacteristicTypes && !this.disableSupplements;

			if (!this.withExperience || !this.isChecked || supplementsEligible)
			{
				return false;
			}

			return !this.hasChilds;
		},
		mustChooseSelfAssessment ()
		{
			const supplementsEligible = this.hasItemCharacteristicTypes && !this.disableSupplements;

			if (!this.withSelfAssessment || !this.isChecked || supplementsEligible)
			{
				return false;
			}

			return !this.hasChilds;
		},
		mustChooseEducation ()
		{
			const disabledForEducation = hasTag(this.characteristic, ADDITIONS_EDUCATION_DISABLE);
			const supplementsEligible = this.hasItemCharacteristicTypes && !this.disableSupplements;

			if (!this.withEducation || !this.isChecked || supplementsEligible || disabledForEducation)
			{
				return false;
			}

			return !this.hasChilds;
		},
		mustChooseRequirement ()
		{
			const supplementsEligible = this.hasItemCharacteristicTypes && !this.disableSupplements;

			if (!this.withRequirement || !this.isChecked || supplementsEligible)
			{
				return false;
			}

			return !this.hasChilds;
		},
		mustChooseSupplements ()
		{
			if (!this.isChecked || !this.hasItemCharacteristicTypes || this.disableSupplements)
			{
				return false;
			}

			return this.nothing || !this.hasChilds;
		},
		supplementsValid ()
		{
			return !this.mustChooseSupplements
				|| (this.supplements || []).length === this.relevantItemCharacteristicTypes.length;
		},
		valid ()
		{
			return this.supplementsValid;
		},
		labels ()
		{
			return get(this.labelSet || 'application');
		},
		label ()
		{
			return get(this.characteristic, this.labelType);
		},
		hasIcon ()
		{
			return this.iconPath && this.characteristic.naturalId;
		},
		searchHits ()
		{
			if (!this.keyword)
			{
				return undefined;
			}

			let hits = _.filter(
				flatten(this.characteristic.children),
				characteristic => search(characteristic, this.keyword)
			);

			return hits.length > 0
				? hits
				: undefined;
		},
		selectedItemsStylingList ()
		{
			return STYLING__LIST;
		},
		isMaxLevelReached ()
		{
			return isDefined(this.maxLevel) && this.maxLevel === this.level;
		},
		supplementAdditions ()
		{
			if (!this.selectedItem?.supplements)
			{
				return undefined;
			}

			return getAsList(this.selectedItem.supplements);
		},
		highlightWords ()
		{
			if (!this.keyword || isEmptyOrNull(this.keyword))
			{
				return undefined;
			}

			return this.keyword
				.split(' ')
				.map(word => word.trim());
		}
	},

	methods: {
		...mapActions('characteristic/fan', [
			'addParent',
		]),
		...mapMutations('characteristic/fan', [
			'setSelected',
		]),
		...mapActions('characteristic/fan', [
			'setPropertyValueForSelectionItem',
		]),
		...mapActions('characteristic/addition/fan', {
			showAdditionFan: 'show'
		}),
		getNothingAllowedTextByLevel (level)
		{
			if (this.nothingAllowed?.textPerLevel && this.nothingAllowed?.textPerLevel[level])
			{
				return this.nothingAllowed?.textPerLevel[level];
			}

			return this.nothingAllowed?.text || 'Alles möglich';
		},
		reSelectCheckbox ()
		{
			this.changeCheckboxSelection(true);
		},
		deSelectCheckbox ()
		{
			this.changeCheckboxSelection(false);
		},
		onDeSelectCharacteristic (characteristic)
		{
			let action = 'Entfernt';

			if (this.characteristicType)
			{
				action = action + ' (' + this.characteristicType.label + ')';
			}

			Event.push(
				new EventData()
					.setEvent(Event.CHARACTERISTIC_SELECTION)
					.setAction(action)
					.setName(characteristic.label)
			);

			this.removeCharacteristic(characteristic);
		},
		changeCheckboxSelection (checked)
		{
			const ref = this.$refs.checkedCheckbox;

			if (ref)
			{
				ref
					.$el
					.getElementsByTagName('input')[0]
					.checked = checked;
			}
		},
		dismissDeselectPopUp ()
		{
			this.reSelectCheckbox();

			this.confirmDeselect = false;
		},
		editAlreadySelected ()
		{
			this.reSelectCheckbox();

			this.addParent({
				characteristic: this.characteristic,
				onClose: this.onCloseParent
			});

			this.confirmDeselect = false;
		},
		removeCharacteristic (characteristic)
		{
			// remove characteristic and all its childs
			this.selected = _.filter(
				this.selected,
				item =>
				{
					const allChildIds = _.map(flatten(characteristic.children), 'id');

					return item.item.id !== characteristic.id && !_.includes(allChildIds, item.item.id);
				}
			);

			this.confirmDeselect = false;
			this.experience = undefined;
			this.selfAssessment = undefined;
			this.educations = undefined;
			this.supplements = undefined;
			this.requirement = undefined;
		},
		checkedChanged (checked)
		{
			if (!checked)
			{
				if (this.hasSelectedChildsToLastLevel)
				{
					this.confirmDeselect = true;

					return;
				}

				this.removeCharacteristic(this.characteristic);

				let action = 'Entfernt';

				if (this.characteristicType)
				{
					action = action + ' (' + this.characteristicType.label + ')';
				}

				Event.push(
					new EventData()
						.setEvent(Event.CHARACTERISTIC_SELECTION)
						.setAction(action)
						.setName(this.characteristic.label)
				);

				return;
			}

			let action = 'Hinzugefügt';

			if (this.characteristicType)
			{
				action = action + ' (' + this.characteristicType.label + ')';
			}

			Event.push(
				new EventData()
					.setEvent(Event.CHARACTERISTIC_SELECTION)
					.setAction(action)
					.setName(this.characteristic.label)
			);

			let selected = this.selected;

			if (!selected)
			{
				selected = [];
			}

			// remove all items on this level and beyond (parents must be kept in tree)
			if (this.singleChoice)
			{
				const parents = getParents(this.characteristic.id, this.characteristicItems);

				let idsToKeep = _.map(parents, 'id');
				idsToKeep.push(this.characteristic.id);

				selected = _.filter(selected, item => _.includes(idsToKeep, item.item.id));
			}

			if (!this.nothing && this.hasChilds)
			{
				if (!this.isSelfChecked && this.isMaxLevelReached)
				{
					if (!_.find(selected, s => s.item.id === this.characteristic.id))
					{
						selected.push({
							item: this.characteristic,
							supplements: this.supplements
						});
					}

					this.addParentsIfNecessary(selected, this.characteristic);

					this.selected = selected;
				}

				if (!this.isMaxLevelReached)
				{
					this.addParent({
						characteristic: this.characteristic,
						onClose: this.onCloseParent
					});
				}
			}
			else
			{
				this.removeCharacteristic(this.characteristic);

				if (!this.isSelfChecked)
				{
					if (!_.find(selected, s => s.item.id === this.characteristic.id))
					{
						selected.push({
							item: this.characteristic,
							supplements: this.supplements
						});
					}
				}

				this.addParentsIfNecessary(selected, this.characteristic);
			}

			if (this.nothing)
			{
				// remove all children
				const allChildIds = _.map(flatten(this.characteristic.children), 'id');

				selected = _.filter(selected, item => !_.includes(allChildIds, item.item.id));
			}

			this.selected = selected;

			if (this.mustChooseAdditions)
			{
				this.editAdditions(true);
			}
		},
		onCloseParent ()
		{
			if (
				!isSelected(this.selected, this.characteristic)
				&& !hasSelectedChildsToLastLevel(this.selected, this.characteristic)
			)
			{
				this.deSelectCheckbox();
			}
		},
		editAdditions (newlyAdded)
		{
			this.showAdditionFan({
				characteristic: this.characteristic,
				item: this.selectedItem || {},
				experienceConfig: {
					enabled: this.withExperience,
					noneAllowed: isDefined(this.experienceConfig?.noneAllowed)
						? this.experienceConfig?.noneAllowed
						: true
				},
				selfAssessmentConfig: {
					enabled: this.withSelfAssessment
				},
				educationConfig: {
					enabled: this.withEducation
				},
				type: this.additionsConfig?.type,
				onClose: data =>
				{
					// remove if unconfirmed
					if (!data.confirmed)
					{
						if (newlyAdded)
						{
							this.removeCharacteristic(this.characteristic);
						}

						return;
					}

					this.setPropertyValueForSelectionItem({
						characteristic: this.characteristic,
						property: 'experience',
						value: data.experience
					});

					this.setPropertyValueForSelectionItem({
						characteristic: this.characteristic,
						property: 'selfAssessment',
						value: data.selfAssessment
					});

					this.setPropertyValueForSelectionItem({
						characteristic: this.characteristic,
						property: 'educationsFinished',
						value: data.educationsFinished
					});

					this.setPropertyValueForSelectionItem({
						characteristic: this.characteristic,
						property: 'educationsUnfinished',
						value: data.educationsUnfinished
					});

					this.setPropertyValueForSelectionItem({
						characteristic: this.characteristic,
						property: 'hasAdditions',
						value: true
					});
				}
			});
		},
		experienceChanged (experience)
		{
			this.setPropertyValueForSelectionItem({
				characteristic: this.characteristic,
				property: 'experience',
				value: experience
			});
		},
		addParentsIfNecessary (selected, characteristic)
		{
			const parents = getParents(
				characteristic.id,
				this.characteristicMetaProperty
					? this.meta[this.characteristicMetaProperty]
					: this.characteristicItems
			);

			_.forEach(parents, parent =>
			{
				if (!_.find(selected, selectedItem => selectedItem.item.id === parent.id))
				{
					selected.push({
						item: parent,
					});
				}
			});
		},
		getInitialSupplements (itemCharacteristicType)
		{
			if (!this.selectedItem)
			{
				return undefined;
			}

			const item = _.find(
				this.selectedItem.supplements ?? [],
				item => item.characteristicType.id === itemCharacteristicType.type.id
			);

			return item?.items ?? [];
		},
		setSupplements (data)
		{
			const characteristicType = data.itemCharacteristicType.type;

			if (!this.supplements)
			{
				this.supplements = [];
			}

			this.supplements = _.filter(
				this.supplements,
				s => s.characteristicType.id !== characteristicType.id
			);

			this.supplements.push({
				characteristicType,
				items: data.items,
			});

			this.setPropertyValueForSelectionItem({
				characteristic: this.characteristic,
				property: 'supplements',
				value: this.supplements
			});
		},
	},

	watch: {
		parents ()
		{
			const inParents = isDefined(
				_.find(this.parents, parent => parent.id === this.characteristic.id)
			);

			if (inParents || !this.isChecked)
			{
				return;
			}

			const childIds = getChildIdsToLastLevel(this.characteristic, this.selected);

			const hasSelectedChilds = _.intersection(
				_.map(this.selected, 'item.id'),
				childIds
			).length > 0;

			// parent closed without any child selected and not "nothing allowed" -> remove from selected
			if (
				this.characteristic.children.length > 0
				&& !this.isNothingAllowed
				&& !hasSelectedChilds
			)
			{
				this.selected = _.filter(this.selected, item => item.item.id !== this.characteristic.id);
			}
		}
	}
}
</script>
